import React, { Suspense, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { Routes, Route, Navigate, Link, matchPath, useLocation, useNavigate, useParams } from 'react-router-dom';
import { Provider } from 'react-redux';
import { Layout, ConfigProvider, App as AntApp, Button, Result, Space, Spin } from 'antd';
import { UserOutlined } from '@ant-design/icons';
import arEG from 'antd/locale/ar_EG';
import deDE from 'antd/locale/de_DE';
import enUS from 'antd/locale/en_US';
import esES from 'antd/locale/es_ES';
import frFR from 'antd/locale/fr_FR';
import hiIN from 'antd/locale/hi_IN';
import itIT from 'antd/locale/it_IT';
import jaJP from 'antd/locale/ja_JP';
import koKR from 'antd/locale/ko_KR';
import plPL from 'antd/locale/pl_PL';
import ptBR from 'antd/locale/pt_BR';
import ruRU from 'antd/locale/ru_RU';
import thTH from 'antd/locale/th_TH';
import trTR from 'antd/locale/tr_TR';
import ukUA from 'antd/locale/uk_UA';
import viVN from 'antd/locale/vi_VN';
import zhCN from 'antd/locale/zh_CN';
import { ThemeProvider, useTheme } from '@context/ThemeContext';
import config from 'react-global-configuration';
import { useTranslation } from 'react-i18next';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { LinkedInCallback } from 'react-linkedin-login-oauth2';
import { PayPalScriptProvider } from '@paypal/react-paypal-js';

import configureStore from '@redux/store';
import Auth from '@auth/Auth';
import PrivateRoute from '@utils/PrivateRoute';
import { addUrlParams, getService } from '@utils/utils';
import HeaderComponent from '@Layout//Header/Header';
import ThemeToggle from '@Layout//Header/components/ThemeToggle/ThemeToggle';
import { ROUTES_CONFIG } from './routes/routesConfig';
import { INCLUDED_SERVICES, ALL_SERVICES_INCLUDED } from '@shared/data/services';
import { ValidationProvider } from '@context/ValidationContext';
import notificationService from '@utils/notificationService';
import modalService from '@utils/modalService';
import { orgConfig } from '@config/index';
import messageService from '@utils/messageService';
import GodModeBanner from '@Components/GodModeBanner/GodModeBanner';
import { getBrandBaseUrl } from '@utils/updateBrandAssets';
import { isSensitiveRoute } from '@utils/sensitiveRoutes';
import { clearSensitiveCredentials } from '@utils/sensitiveCredentials';
import {
  getUrlLanguageParam,
  normalizeAntdLocaleKey,
  normalizeHtmlLanguage,
  normalizeI18nLanguage,
} from '@utils/languageContract';

const Signup = React.lazy(() => import('@auth/Signup'));
const ReferrerRedirect = React.lazy(() => import('@auth/ReferrerRedirect'));
const PartnerRedirect = React.lazy(() => import('@auth/PartnerRedirect'));
const Login = React.lazy(() => import('@auth/Login'));
const OAuthAuthorize = React.lazy(() => import('@auth/OAuthAuthorize'));
const ResetPassword = React.lazy(() => import('@auth/ResetPassword'));
const ResetPasswordConfirm = React.lazy(() => import('@auth/ResetPasswordConfirm'));
const AcceptInvitation = React.lazy(() => import('@auth/AcceptInvitation'));
const KYC = React.lazy(() => import('@auth/KYC'));
const Logout = React.lazy(() => import('@auth/Logout'));
const Subscribe = React.lazy(() => import('@auth/Subscribe'));
const VerifyEmail = React.lazy(() => import('@auth/VerifyEmail'));
const DomainContacts = React.lazy(() => import('@Components/Services/Enrichers/Lookup/DomainContacts/DomainContacts'));
const EmailValidator = React.lazy(() => import('@Components/Services/Enrichers/Lookup/EmailValidator/EmailValidator'));
const PlacesUpdate = React.lazy(() => import('@Components/Services/PlacesUpdate'));
const PlacesSearch = React.lazy(() => import('@Components/Services/PlacesSearch'));
const DomainContactsFinder = React.lazy(() => import('@Components/Services/DomainContactsFinder'));
const FreeEmailsValidator = React.lazy(() => import('@Components/Services/Enrichers/Lookup/EmailValidator/EmailsValidator'));
const FreeEmailsFinder = React.lazy(() => import('@Components/Services/Enrichers/Lookup/EmailsFinder'));
const LiveResults = React.lazy(() => import('./pages/Tasks/LiveResults/LiveResults'));
const Tasks = React.lazy(() => import('./pages/Tasks/Tasks'));
const SharedTaskResults = React.lazy(() => import('./pages/Tasks/SharedTaskResults/SharedTaskResults'));
const Profile = React.lazy(() => import('./pages/Profile/Profile'));
const ApiKey = React.lazy(() => import('./pages/ApiKey/ApiKey'));
const ApiUsage = React.lazy(() => import('./pages/ApiUsage/ApiUsage'));
const ApiDocs = React.lazy(() => import('./pages/ApiDocs/ApiDocs'));
const WebhookCalls = React.lazy(() => import('./pages/WebhookCalls/WebhookCalls'));
const MakeCryptoPayment = React.lazy(() => import('@Components/Billing/Crypto/MakeCryptoPayment'));
const CryptoPayment = React.lazy(() => import('@Components/Billing/Crypto/CryptoPayment'));
const Integrations = React.lazy(() => import('./pages/Integrations/Integrations'));
const Billing = React.lazy(() => import('./pages/Billing/Billing'));
const ServicesPage = React.lazy(() => import('./pages/Services/ServicesPage'));
const ServicesWidgetPage = React.lazy(() => import('./pages/ServicesWidgetPage/ServicesWidgetPage'));
const DatasetsPage = React.lazy(() => import('./pages/DatasetsPage/DatasetsPage'));
const Catalog = React.lazy(() => import('./pages/Catalog/Catalog'));
const CheckoutHistory = React.lazy(() => import('./pages/Catalog/CheckoutHistory/CheckoutHistory'));
const CatalogExportSuccess = React.lazy(() => import('./pages/Catalog/ExportSuccess/ExportSuccess'));
const ExportHistory = React.lazy(() => import('./pages/Catalog/ExportHistory/ExportHistory'));
const CatalogSubscriptionLink = React.lazy(() => import('./pages/Catalog/SubscriptionLink/CatalogSubscriptionLink'));
const SimilarCompaniesFinder = React.lazy(() => import( './pages/SimilarCompaniesFinder/SimilarCompaniesFinder'));

const auth = new Auth();
const googleClientId = config.get('googleClientId');
const payPalClientId = config.get('payPalClientId');

const PLAIN_PAGE_PATHS = [
  '/domain-contacts-lookup',
  '/email-validator-lookup',
  '/emails-validator-lookup',
  '/r',
  '/refer',
  '/signup-confirm',
  '/emails-finder-lookup',
  '/login',
  '/signup',
  '/reset-password',
  '/reset-password/:token',
  '/accept-invite/:token',
  '/accept-invite/confirm',
  '/partner-redirect/:token',
  '/oauth/authorize',
  '/services-widget',
  '/shared/:userId/:taskId',
  '/linkedin-callback',
];

const isPlainPagePath = (pathname) => PLAIN_PAGE_PATHS.some(
  path => matchPath({ path, end: true }, pathname)
);

const store = configureStore();

const paypalOptions = {
  clientId: payPalClientId,
  disableFunding: 'paylater',
  components: 'buttons',
  currency: 'USD'
};

const PUBLIC_CATALOG_PATHS = new Set(['/businesses', '/businesses/similar', '/catalog', '/checkout-history']);
const ACCOUNT_TABS = ['settings', 'api'];
const BILLING_TABS = ['payments', 'info', 'invoices'];

function PageLoader() {
  return <div className='center'><Spin /></div>;
}

function BusinessesRedirect() {
  const { search, hash } = useLocation();

  return <Navigate to={{ pathname: '/businesses', search, hash }} replace />;
}

function ValidTabRoute({ validTabs, fallbackPath, children }) {
  const { tab } = useParams();
  const { search, hash } = useLocation();

  if (!validTabs.includes(tab)) {
    return <Navigate to={{ pathname: fallbackPath, search, hash }} replace />;
  }

  return children;
}

ValidTabRoute.propTypes = {
  validTabs: PropTypes.arrayOf(PropTypes.string).isRequired,
  fallbackPath: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
};

const antdLocales = {
  ar: arEG,
  de: deDE,
  en: enUS,
  es: esES,
  fr: frFR,
  hi: hiIN,
  it: itIT,
  ja: jaJP,
  jp: jaJP,
  ko: koKR,
  pl: plPL,
  'pt-BR': ptBR,
  pt: ptBR,
  ru: ruRU,
  th: thTH,
  tr: trTR,
  uk: ukUA,
  vi: viVN,
  zh: zhCN,
};

export default function App() {
  const location = useLocation();
  const sensitiveRoute = isSensitiveRoute(location);
  const wasSensitiveRoute = useRef(sensitiveRoute);
  const app = <ThemedApp />;

  useEffect(() => {
    const leftSensitiveRoute = wasSensitiveRoute.current && !sensitiveRoute;
    wasSensitiveRoute.current = sensitiveRoute;

    if (!leftSensitiveRoute) return;

    clearSensitiveCredentials();

    if (window.__AYDA_SENSITIVE_ROUTE__ === true) {
      window.location.replace(`${location.pathname}${location.search}${location.hash}`);
    }
  }, [location.hash, location.pathname, location.search, sensitiveRoute]);

  return (
    <Provider store={store}>
      <ThemeProvider>
        {sensitiveRoute ? app : (
          <PayPalScriptProvider options={paypalOptions}>
            {app}
          </PayPalScriptProvider>
        )}
      </ThemeProvider>
    </Provider>
  );
}

function ThemedApp() {
  const orgTheme = orgConfig?.theme || { colorPrimary: '#1677FF' };
  const { algorithm } = useTheme();
  const { t, i18n } = useTranslation();
  const selectedLanguage = normalizeI18nLanguage(i18n.resolvedLanguage || i18n.language || 'en');
  const antdLocaleKey = normalizeAntdLocaleKey(selectedLanguage);
  const htmlLanguage = normalizeHtmlLanguage(selectedLanguage);
  const direction = i18n.dir(selectedLanguage);

  useEffect(() => {
    document.documentElement.lang = htmlLanguage;
    document.documentElement.dir = direction;
  }, [direction, htmlLanguage]);

  return (
    <ConfigProvider
      direction={direction}
      locale={antdLocales[antdLocaleKey] || enUS}
      modal={{ closable: { 'aria-label': t('action.close') } }}
      theme={{
        algorithm: algorithm,
        token: {
          colorPrimary: orgTheme.colorPrimary,
        },
      }}
    >
      <ValidationProvider>
        <AntApp>
          <AppContent />
        </AntApp>
      </ValidationProvider>
    </ConfigProvider>
  );
}

function AppContent() {
  const location = useLocation();
  const navigate = useNavigate();
  const { pathname } = location;
  const { profile } = auth;
  const { ie, uiDomain } = profile;
  const sensitiveRoute = isSensitiveRoute(location);

  const isPublicCatalogPath = PUBLIC_CATALOG_PATHS.has(pathname);
  const catalogQueryParams = new URLSearchParams(location.search);
  const explicitPublicCatalogPage = catalogQueryParams.has('hideHeader') || catalogQueryParams.has('hideLogo');
  const isPublicCatalogPage = isPublicCatalogPath && (
    pathname === '/checkout-history' || !auth.authenticated || explicitPublicCatalogPage
  );
  const showSider = !isPublicCatalogPage && !isPlainPagePath(pathname);
  const { i18n } = useTranslation();

  const { notification: notificationApi, modal: modalApi, message: messageApi, contextHolder } = AntApp.useApp();

  useEffect(() => {
    notificationService.setNotification(notificationApi);
    messageService.setMessage(messageApi);
    modalService.setModal(modalApi);
  }, [notificationApi, modalApi]);

  useEffect(() => {
    notificationApi.destroy();
  }, [location.pathname]);

  useEffect(() => {
    if (sensitiveRoute) return;
    if (!showSider && pathname !== '/login' && pathname !== '/signup') return;

    const language = getUrlLanguageParam(i18n.resolvedLanguage || i18n.language);
    const newParams = addUrlParams(window.location.search, { ln: language }, true);
    const nextSearch = newParams ? `?${newParams}` : '';

    if (nextSearch === window.location.search) return;

    navigate({
      pathname: window.location.pathname,
      search: nextSearch,
      hash: window.location.hash,
    }, { replace: true });
  }, [i18n.language, location.search, navigate, pathname, sensitiveRoute, showSider]);

  useEffect(() => {
    if (localStorage.getItem('isLoggedIn') === '1') {
      const expiresAt = localStorage.getItem('expiresAt');

      if (expiresAt) {
        const expiresAtTimestamp = Number(expiresAt);
        const now = new Date().getTime();

        if (expiresAtTimestamp <= now) {
          console.log('Token expired on mount, cleaning up');
          localStorage.removeItem('isLoggedIn');
          localStorage.removeItem('expiresAt');
          localStorage.removeItem('accessToken');

          if (pathname !== '/login' && pathname !== '/signup' && !isPublicCatalogPage && !isPlainPagePath(pathname)) {
            window.location.href = '/login';
          }
          return;
        }

        const differenceInDays = (expiresAtTimestamp - now) / 1000 / 24 / 60 / 60;
        if (differenceInDays < 29) {
          console.log('Token expiring soon, renewing session');
          auth.renewSession().catch(err => {
            console.error('Failed to renew session on mount:', err);
          });
        }
      } else {
        console.log('Invalid state: isLoggedIn without expiresAt');
        localStorage.removeItem('isLoggedIn');
        if (pathname !== '/login' && pathname !== '/signup' && !isPublicCatalogPage && !isPlainPagePath(pathname)) {
          window.location.href = '/login';
        }
      }
    }

    const intervalId = setInterval(
      () => {
        if (localStorage.getItem('isLoggedIn') === '1') {
          const expiresAt = localStorage.getItem('expiresAt');

          if (expiresAt) {
            const expiresAtTimestamp = Number(expiresAt);
            const now = new Date().getTime();

            if (expiresAtTimestamp <= now) {
              console.log('Token expired in interval, cleaning up');
              localStorage.removeItem('isLoggedIn');
              localStorage.removeItem('expiresAt');
              localStorage.removeItem('accessToken');

              const currentPath = window.location.pathname;
              if (!PUBLIC_CATALOG_PATHS.has(currentPath) && !isPlainPagePath(currentPath)) {
                window.location.href = '/login';
              }
            } else {
              auth.renewSession().catch(err => {
                console.error('Failed to renew session in interval:', err);
              });
            }
          } else {
            localStorage.removeItem('isLoggedIn');
          }
        }
      },
      1000 * 60 * 60 * 12
    );

    return () => clearInterval(intervalId);
  }, []);

  const isEnabled = (serviceKey) => {
    if (ALL_SERVICES_INCLUDED) return true;
    if (!serviceKey) return false;

    const service = getService(serviceKey);

    if (!service) return false;

    return INCLUDED_SERVICES.has(service.logicalKey);
  };

  function BillingRedirect() {
    const location = useLocation();
    return <Navigate to={`/billing/payments${location.search}`} replace />;
  }

  function AccountRedirect() {
    const location = useLocation();
    return <Navigate to={`/account/settings${location.search}`} replace />;
  }

  return (
    <Provider store={store}>
      {showSider && (
        <Layout style={{ minHeight: '100vh' }}>
          {ie && <GodModeBanner ie={ie} uiDomain={uiDomain} auth={auth} />}
          <HeaderComponent auth={auth} isGodMode={!!ie} />
          <Layout>
            <Layout.Content>
              <div className='container'>
                <Suspense fallback={<PageLoader />}>
                  <Routes>
                    <Route path='/' element={<Navigate to='/billing' replace />} />

                    <Route path='/profile' element={<BillingRedirect />} />
                    <Route path='/billing-info' element={<Navigate to='/billing/info' replace />}/>
                    <Route path='/invoices' element={<Navigate to='/billing/invoices' replace />}/>

                    <Route path='/account' element={<AccountRedirect />}/>
                    <Route path='/account/:tab' element={
                      <ValidTabRoute validTabs={ACCOUNT_TABS} fallbackPath='/account/settings'>
                        <PrivateRoute element={Profile} auth={auth} />
                      </ValidTabRoute>
                    }/>

                    <Route path='/billing' element={<BillingRedirect />} />

                    <Route
                      path='/billing/crypto-payment'
                      element={<PrivateRoute element={MakeCryptoPayment} />}
                    />
                    <Route
                      path='/billing/crypto-payment/:paramId'
                      element={<PrivateRoute element={CryptoPayment} />}
                    />
                    <Route path='/billing/:tab' element={
                      <ValidTabRoute validTabs={BILLING_TABS} fallbackPath='/billing/payments'>
                        <PrivateRoute element={Billing} auth={auth} />
                      </ValidTabRoute>
                    }/>

                    <Route path='/services' element={<ServicesPage />} />
                    <Route path='/services/all' element={<ServicesPage />} />
                    <Route path='/services/favorites' element={<ServicesPage />} />
                    <Route path='/services/use-cases' element={<ServicesPage />} />
                    <Route path='/services/use-cases/:useCaseKey' element={<ServicesPage />} />

                    <Route path='/datasets' element={<PrivateRoute element={DatasetsPage} auth={auth} />} />
                    <Route path='/datasets/my' element={<PrivateRoute element={DatasetsPage} auth={auth} />} />
                    <Route path='/datasets/my/published' element={<PrivateRoute element={DatasetsPage} auth={auth} />} />
                    <Route path='/datasets/my/pending' element={<PrivateRoute element={DatasetsPage} auth={auth} />} />
                    <Route path='/datasets/my/purchased' element={<PrivateRoute element={DatasetsPage} auth={auth} />} />
                    <Route path='/datasets/admin' element={<PrivateRoute element={DatasetsPage} auth={auth} />} />
                    <Route path='/datasets/:datasetId' element={<PrivateRoute element={DatasetsPage} auth={auth} />} />
                    <Route path='/catalog' element={<Navigate to='/businesses' replace />} />
                    <Route path='/local-businesses' element={<BusinessesRedirect />} />
                    <Route path='/businesses' element={<Catalog auth={auth} />} />
                    <Route path='/businesses/similar' element={<SimilarCompaniesFinder auth={auth} />} />
                    <Route path='/businesses/subscriptions-link/:token' element={<PrivateRoute element={CatalogSubscriptionLink} auth={auth} />} />
                    <Route path='/businesses/export/history' element={<PrivateRoute element={ExportHistory} auth={auth} />} />
                    <Route path='/businesses/export/success' element={<PrivateRoute element={CatalogExportSuccess} auth={auth} />} />

                    <Route path='/contacts-finder' element={<Navigate to='/leads-n-contacts' replace />} />
                    <Route path='/contacts-n-leads' element={<Navigate to='/leads-n-contacts' replace />} />
                    <Route
                      path='/api-key'
                      element={<PrivateRoute element={ApiKey} />}
                    />
                    <Route
                      path='/usage-history'
                      element={<PrivateRoute element={ApiUsage} />}
                    />
                    <Route
                      path='/api-usage'
                      element={<Navigate to='/usage-history' replace />}
                    />
                    <Route
                      path='/webhook-calls'
                      element={<PrivateRoute element={WebhookCalls} />}
                    />
                    <Route
                      path='/tasks'
                      element={<PrivateRoute element={Tasks}  />}
                    />
                    <Route
                      path='/tasks/:taskId/live-results'
                      element={<PrivateRoute element={LiveResults} />}
                    />
                    <Route
                      path='/places-update'
                      element={<PrivateRoute element={PlacesUpdate} />}
                    />
                    <Route
                      path='/places-search'
                      element={<PrivateRoute element={PlacesSearch} />}
                    />
                    <Route
                      path='/domain-contacts-finder'
                      element={<PrivateRoute element={DomainContactsFinder} />}
                    />

                    <Route path='/integrations' element={<Integrations />} />
                    <Route path='/api-docs' element={<ApiDocs auth={auth} />} />

                    <Route path='/logout' element={<Logout auth={auth} />} />
                    <Route path='/callback' element={<Logout auth={auth} />} />
                    <Route path='/verify-email/:token' element={<VerifyEmail />} />
                    <Route
                      path='/subscribe'
                      element={<Subscribe auth={auth} />}
                    />
                    {ROUTES_CONFIG.map(({ path, element: Element, ...rest }) => (
                      isEnabled(rest.serviceKey) ? (
                        <Route
                          key={path}
                          path={path}
                          element={
                            <PrivateRoute
                              element={Element}
                              auth={auth}
                              {...rest}
                            />
                          }
                        />
                      ) : null
                    ))}
                    <Route path='*' element={<NotFoundPage />} />
                  </Routes>
                </Suspense>
              </div>
            </Layout.Content>
          </Layout>
        </Layout>
      )}
      {isPublicCatalogPage && (
        <>
          <CatalogPublicHeader auth={auth} />
          <div className='container businesses-public-content'>
            <Suspense fallback={<PageLoader />}>
              <Routes>
                <Route path='/catalog' element={<Navigate to='/businesses' replace />} />
                <Route path='/local-businesses' element={<BusinessesRedirect />} />
                <Route path='/businesses' element={<Catalog auth={auth} />} />
                <Route path='/businesses/similar' element={<SimilarCompaniesFinder auth={auth} />} />
                <Route path='/checkout-history' element={<CheckoutHistory />} />
              </Routes>
            </Suspense>
          </div>
        </>
      )}
      {(!sensitiveRoute || pathname === '/login') && (
        <GoogleOAuthProvider clientId={googleClientId}>
          <Suspense fallback={<PageLoader />}>
            <Routes>
              <Route path='/login' element={<Login auth={auth} />} />
              <Route path='/signup' element={<Signup auth={auth} />} />
            </Routes>
          </Suspense>
        </GoogleOAuthProvider>
      )}
      <Suspense fallback={<PageLoader />}>
        <Routes>
          <Route path='/oauth/authorize' element={<OAuthAuthorize auth={auth} />} />
          <Route
            path='/reset-password'
            element={<ResetPassword />}
          />
          <Route
            path='/reset-password/confirm'
            element={<ResetPasswordConfirm />}
          />
          <Route
            path='/reset-password/:token'
            element={<ResetPasswordConfirm />}
          />
          <Route
            path='/accept-invite/confirm'
            element={<AcceptInvitation auth={auth} />}
          />
          <Route
            path='/accept-invite/:token'
            element={<AcceptInvitation auth={auth} />}
          />
          <Route path='/r' element={<ReferrerRedirect />} />
          <Route path='/refer' element={<ReferrerRedirect />} />
          <Route path='/partner-redirect/:token' element={<PartnerRedirect auth={auth} />} />
          <Route path='/signup-confirm' element={<KYC auth={auth} />} />
          <Route path='/shared/:userId/:taskId' element={<SharedTaskResults />} />
          <Route path='/domain-contacts-lookup' element={<DomainContacts />} />
          <Route path='/email-validator-lookup' element={<EmailValidator />} />
          <Route path='/emails-validator-lookup' element={<FreeEmailsValidator />} />
          <Route path='/emails-finder-lookup' element={<FreeEmailsFinder />} />
          <Route path='/linkedin-callback' element={<LinkedInCallback />} />
          <Route path='/services-widget' element={<ServicesWidgetPage />} />
          <Route path='*' element={null} />
        </Routes>
      </Suspense>
      {contextHolder}
    </Provider>
  );
}

function NotFoundPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();

  return (
    <Result
      status='404'
      title='404'
      subTitle={t('errors.notFound')}
      extra={<Button type='primary' onClick={() => navigate('/')}>{t('title.back')}</Button>}
    />
  );
}

function CatalogPublicHeader({ auth }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { isDarkMode } = useTheme();
  const queryParams = new URLSearchParams(location.search);

  if (queryParams.has('hideHeader')) return null;

  const baseUrl = getBrandBaseUrl();
  const getLogoUrl = (fileName) => baseUrl === '/' ? `/${fileName}` : `${baseUrl}/${fileName}`;
  const redirect = location.pathname + location.search;
  const profile = auth.profile || {};
  const accountLabel = profile.name || profile.email || t('title.account');

  const openAuthPath = (path) => {
    if (window.self !== window.top) {
      window.open(path, '_blank')?.focus();
      return;
    }

    navigate(path);
  };

  const login = () => openAuthPath(`/login?redirect=${encodeURIComponent(redirect)}`);
  const account = () => openAuthPath('/account/settings');

  return (
    <Layout.Header className='businesses-header'>
      <div className='container'>
        <div>
          {!queryParams.has('hideLogo') && (
            <Link to='/' className='logo'>
              <img
                src={getLogoUrl(isDarkMode ? 'logo-dark.svg' : 'logo-light.svg')}
                onError={event => {
                  event.currentTarget.onerror = null;
                  event.currentTarget.src = getLogoUrl('logo.svg');
                }}
                alt={config.get('company')}
              />
            </Link>
          )}
        </div>
        <Space size={10} align='center' className='businesses-header__actions'>
          <ThemeToggle compact />
          {auth.authenticated ? (
            <Button
              type='link'
              icon={<UserOutlined />}
              className='businesses-header__account'
              title={accountLabel}
              onClick={account}
            >
              <span className='businesses-header__account-label'>{accountLabel}</span>
            </Button>
          ) : (
            <Button type='link' icon={<UserOutlined />} onClick={login}>{t('title.logIn')}</Button>
          )}
        </Space>
      </div>
    </Layout.Header>
  );
}

CatalogPublicHeader.propTypes = {
  auth: PropTypes.shape({
    authenticated: PropTypes.bool,
    profile: PropTypes.object,
  }).isRequired,
};
